import {
  DefaultFetchError,
  FetchCreate,
  FetchDelete,
  FetchGet,
  FetchGetId,
  FetchSuccess,
  FetchUpdate,
  PagingDataResponse,
  PagingParams,
  useFetchCreate,
  useFetchDelete,
  useFetchGet,
  useFetchGetId,
  useFetchUpdate,
} from '../fetch';

export interface Periods extends GuaranteesInfo {
  id?: string;
  /** ISO 8601 from API; use dayjs(...) in forms for DatePicker */
  startDate: string;
  endDate: string;
  // isPriceOverviewVisible: boolean;
  // priceOverview: PriceOverviewInfo;
  // tooltips: TariffTooltipsInfo;
}

export interface Tariff extends GuaranteesInfo {
  id: string;
  name: string;
  tariffId: number;
  isActive: boolean; // Is Invite friends active
  isPriceOverviewVisible: boolean;

  priceOverview: PriceOverviewInfo;
  tooltips: TariffTooltipsInfo;

  periods: Periods[]

  brevoListId: number | null;
  invitationPageUrl: string | null;
}

export interface GuaranteesInfo {
  basicPrice: number | null; // (grundgebuhr/Grundpreis)
  basicPricePeriod: number | null; // Grundpreis period.

  energyPrice: number | null; // Energiepreis
  energyPricePeriod: number | null; // Energiepreis period

  numberOfMonths: number | null; // Netto-Preisgarantie period
}

export interface PriceOverviewInfo { // Preisübersicht
  energyPriceLabel: string; // Energiepreis netto
  energyPriceValue: string;
  bonusLabel: string; // 15% grüüün Bonus
  bonusValue: string;
}

export interface TariffTooltipsInfo {
  guaranties?: {
    title: string;
    description: string;
  },
}

export interface TariffTableRow extends Tariff {
  key: string;
}

export interface TariffGetParams extends PagingParams, Partial<Omit<Tariff, 'id'>> {
  orderByColumn?: keyof Omit<Tariff, 'id'>;
}

export type TariffUpsertParams = Omit<Tariff, 'id'>;

export const useTariffsGet = <DD = PagingDataResponse<Tariff>>(
  decorateData?: (data: PagingDataResponse<Tariff>) => DD,
): FetchGet<
  PagingDataResponse<Tariff>,
  TariffGetParams,
  DefaultFetchError,
  DD
> => useFetchGet('tariffs', {
    autoStart: false,
    startStateLoading: false,
    decorateData,
    config: {
      params: {
        // Add default params to filter by tariffId & ASC
        ...{
          orderBy: 'ASC',
          orderByColumn: 'tariffId',
        },
      },
    },
  });

export const useTariffGetById = (
  id?: string,
): FetchGetId<Tariff, DefaultFetchError, unknown> => useFetchGetId('tariffs', id, { autoStart: !!id });

export const useTariffCreate = (): FetchCreate<
  Tariff,
  DefaultFetchError,
  TariffUpsertParams
> => useFetchCreate('tariffs');

export const useTariffUpdate = (
  id?: string,
): FetchUpdate<Tariff, DefaultFetchError, TariffUpsertParams> => useFetchUpdate('tariffs', id);

export const useTariffDelete = (
  id?: string,
): FetchDelete<FetchSuccess, DefaultFetchError> => useFetchDelete('tariffs', id);

export interface TariffsTableData {
  data: TariffTableRow[];
  total: number;
}

export const useTariffsTableGet = () => useTariffsGet<TariffsTableData>(
  ({ data, meta }: PagingDataResponse<Tariff>): TariffsTableData => ({
    data: data.map((item) => ({
      key: item.id,
      ...item,
    })),
    total: meta.itemCount,
  }),
);
